/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode, ContentStatus } from '@confluence/page-utils/entry-points/enums';
import type { ContentState } from '@confluence/content-state';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { normalizeContentStatusData } from '../normalizers/normalizeContentStatusData';

import type {
	useContentStateDetailsPanelSingleContentStateQuery as useContentStateDetailsPanelSingleContentStateQuery$data,
	useContentStateDetailsPanelSingleContentStateQueryVariables as useContentStateDetailsPanelSingleContentStateQuery$variables,
} from './__types__/useContentStateDetailsPanelSingleContentStateQuery';

const useContentStateDetailsPanelSingleContentStateQuery = gql`
	query useContentStateDetailsPanelSingleContentStateQuery(
		$contentId: ID!
		$isDraft: Boolean!
		$status: String!
	) {
		singleContent(id: $contentId, status: [$status]) {
			contentState(isDraft: $isDraft) {
				id
				name
				color
				restrictionLevel
			}
		}
	}
`;

export type AvailableContentState = ContentState & {
	isContentStateAvailable: boolean;
};

export const useContentState = ({
	isContentStateAvailable,
	...contentState
}: AvailableContentState) => {
	const [contentId] = usePageContentId();
	const pageMode = useGetPageMode();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus({ skip: isContentStateAvailable });

	const { data, loading, error, refetch } = useQuery<
		useContentStateDetailsPanelSingleContentStateQuery$data,
		useContentStateDetailsPanelSingleContentStateQuery$variables
	>(useContentStateDetailsPanelSingleContentStateQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
			isDraft: contentStatus === ContentStatus.DRAFT || pageMode === PageMode.EDIT,
		},
		skip:
			isContentStateAvailable ||
			isContentStatusLoading ||
			!!contentStatusError ||
			!contentStatus ||
			!contentId,
	});
	const normalizedData = useMemo(() => normalizeContentStatusData(data), [data]);

	return {
		data: isContentStateAvailable ? contentState : normalizedData,
		loading,
		error: contentStatusError || error,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
	};
};
